import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Calendar as CalIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  livre:      { label: 'Livre',      dot: 'bg-white/15',           card: 'border-white/[0.06] bg-white/[0.015]', text: 'text-white/45' },
  reservado:  { label: 'Reservado',  dot: 'bg-yellow-400',         card: 'border-yellow-500/30 bg-yellow-500/[0.06]', text: 'text-yellow-300' },
  fechado:    { label: 'Fechado',    dot: 'bg-emerald-400',        card: 'border-emerald-500/30 bg-emerald-500/[0.06]', text: 'text-emerald-300' },
  bloqueado:  { label: 'Bloqueado',  dot: 'bg-rose-400',           card: 'border-rose-500/30 bg-rose-500/[0.06]', text: 'text-rose-300' },
};

const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WEEK_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CalendarPage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [notes, setNotes] = useState('');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const todayStr = new Date().toISOString().slice(0, 10);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/calendar?month=${month + 1}&year=${year}`);
      setEvents(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, [month, year]);

  const eventsByDate = useMemo(() => {
    const m = {};
    events.forEach((e) => { m[e.date] = e; });
    return m;
  }, [events]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => {
    const t = new Date();
    setCurrentDate(new Date(t.getFullYear(), t.getMonth(), 1));
  };

  const selectDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setNotes(eventsByDate[dateStr]?.notes || '');
  };

  const setDayStatus = async (status) => {
    if (!selectedDate) return;
    try {
      await axios.post(`${API}/calendar`, { date: selectedDate, status, notes });
      fetchEvents();
    } catch (e) { console.error(e); }
  };

  const saveNotes = async () => {
    if (!selectedDate) return;
    const current = eventsByDate[selectedDate]?.status || 'livre';
    try {
      await axios.post(`${API}/calendar`, { date: selectedDate, status: current, notes });
      fetchEvents();
    } catch (e) { console.error(e); }
  };

  // Build leading empty cells
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // Summary counts for the month
  const summary = useMemo(() => {
    const counts = { livre: 0, reservado: 0, fechado: 0, bloqueado: 0 };
    Object.values(eventsByDate).forEach((e) => { if (counts[e.status] !== undefined) counts[e.status]++; });
    counts.livre = daysInMonth - (counts.reservado + counts.fechado + counts.bloqueado);
    return counts;
  }, [eventsByDate, daysInMonth]);

  const selectedEvent = selectedDate ? eventsByDate[selectedDate] : null;
  const selectedStatus = selectedEvent?.status || 'livre';

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-body">
      <header className="bg-[#111111] border-b border-white/5 px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/40 hover:text-white transition-colors" data-testid="back-to-dashboard">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <CalIcon className="w-4 h-4 text-[#C9A961]" />
              <h1 className="font-display text-lg text-white">Calendário <span className="text-[#C9A961]">de eventos</span></h1>
            </div>
          </div>
          <button onClick={goToday} className="font-body text-[11px] tracking-wider uppercase text-white/40 hover:text-[#C9A961] transition-colors px-3 py-1.5 border border-white/10 rounded-sm hover:border-[#C9A961]/40" data-testid="today-btn">
            Hoje
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* === Calendar === */}
          <div>
            {/* Month Header */}
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} data-testid="prev-month-btn" className="w-9 h-9 rounded-full border border-white/8 flex items-center justify-center text-white/45 hover:text-white hover:border-[#C9A961]/40 transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-center">
                <h2 className="font-display text-2xl md:text-3xl text-white">{MONTH_NAMES[month]}</h2>
                <span className="font-body text-[11px] tracking-[0.25em] uppercase text-[#C9A961]/70">{year}</span>
              </div>
              <button onClick={nextMonth} data-testid="next-month-btn" className="w-9 h-9 rounded-full border border-white/8 flex items-center justify-center text-white/45 hover:text-white hover:border-[#C9A961]/40 transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Summary chips */}
            <div className="flex flex-wrap gap-2 mb-5">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <div key={key} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${meta.card}`}>
                  <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                  <span className={`text-[10px] uppercase tracking-wider font-medium ${meta.text}`}>{meta.label}</span>
                  <span className="text-[11px] text-white/40 tabular-nums">{summary[key]}</span>
                </div>
              ))}
            </div>

            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {WEEK_LABELS.map((d) => (
                <div key={d} className="text-center py-2 text-[10px] uppercase tracking-[0.2em] text-white/30 font-medium">{d}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-2">
              {cells.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} className="aspect-square" />;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const evt = eventsByDate[dateStr];
                const status = evt?.status || 'livre';
                const meta = STATUS_META[status] || STATUS_META.livre;
                const isSelected = selectedDate === dateStr;
                const isToday = todayStr === dateStr;

                return (
                  <button
                    key={dateStr}
                    onClick={() => selectDay(day)}
                    data-testid={`day-${dateStr}`}
                    className={`group aspect-square relative flex flex-col items-start justify-between p-2 sm:p-2.5 rounded-md border transition-all duration-200 ${meta.card}
                      ${isSelected ? 'ring-1 ring-[#C9A961] scale-[1.02] shadow-lg shadow-[#C9A961]/15' : 'hover:border-[#C9A961]/35 hover:-translate-y-0.5'}`}
                  >
                    <span className={`text-sm sm:text-base font-medium tabular-nums ${isToday ? 'text-[#C9A961]' : 'text-white/80'}`}>
                      {day}
                    </span>
                    {isToday && <span className="absolute top-1.5 right-1.5 text-[8px] uppercase tracking-wider text-[#C9A961]/80 font-medium">hoje</span>}
                    {evt && evt.status !== 'livre' && (
                      <div className="flex items-center gap-1 mt-auto">
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                        <span className={`text-[9px] uppercase tracking-wider ${meta.text}`}>{meta.label.slice(0, 3)}</span>
                      </div>
                    )}
                    {evt?.notes && <span className="absolute bottom-1.5 right-1.5 w-1 h-1 rounded-full bg-[#C9A961]/60" title="Tem anotação" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* === Detail sidebar === */}
          <aside className="lg:sticky lg:top-6 self-start">
            {!selectedDate ? (
              <div className="p-6 rounded-md border border-white/5 bg-[#111111] text-center">
                <CalIcon className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <p className="font-body text-sm text-white/45 mb-1">Selecione um dia</p>
                <p className="font-body text-[11px] text-white/25">Para gerenciar disponibilidade e bloqueios.</p>
              </div>
            ) : (
              <div className="rounded-md border border-white/5 bg-[#111111] overflow-hidden" data-testid="day-detail-panel">
                <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                  <div>
                    <span className="font-body text-[10px] uppercase tracking-wider text-white/30 block">Dia selecionado</span>
                    <span className="font-display text-lg text-white">
                      {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}
                    </span>
                  </div>
                  <button onClick={() => setSelectedDate(null)} className="text-white/30 hover:text-white transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  {/* Current status badge */}
                  <div>
                    <span className="font-body text-[10px] uppercase tracking-wider text-white/30 block mb-1.5">Status atual</span>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${STATUS_META[selectedStatus].card}`}>
                      <span className={`w-2 h-2 rounded-full ${STATUS_META[selectedStatus].dot}`} />
                      <span className={`text-[11px] uppercase tracking-wider font-medium ${STATUS_META[selectedStatus].text}`}>
                        {STATUS_META[selectedStatus].label}
                      </span>
                    </div>
                  </div>

                  {/* Status pickers */}
                  <div>
                    <span className="font-body text-[10px] uppercase tracking-wider text-white/30 block mb-2">Alterar para</span>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(STATUS_META).map(([key, meta]) => (
                        <button
                          key={key}
                          onClick={() => setDayStatus(key)}
                          data-testid={`set-status-${key}`}
                          className={`px-3 py-2 rounded-sm border text-[11px] uppercase tracking-wider font-medium transition-all ${
                            selectedStatus === key
                              ? `${meta.card} ${meta.text}`
                              : 'border-white/8 text-white/40 hover:border-[#C9A961]/30 hover:text-white/70'
                          }`}
                        >
                          {meta.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <span className="font-body text-[10px] uppercase tracking-wider text-white/30 block mb-1.5">Observações</span>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Ex: visita técnica, cliente VIP, prova de menu..."
                      data-testid="day-notes-input"
                      className="w-full px-3 py-2 rounded-sm bg-[#0a0a0a] border border-white/10 text-white/75 font-body text-sm focus:border-[#C9A961]/40 focus:outline-none resize-none h-24 placeholder:text-white/20"
                    />
                    <button
                      onClick={saveNotes}
                      data-testid="save-notes-btn"
                      className="mt-2 w-full px-3 py-2 rounded-sm bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body text-[11px] uppercase tracking-wider font-semibold hover:shadow-lg hover:shadow-[#C9A961]/25 transition-all"
                    >
                      Salvar observação
                    </button>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
